import cx from "classnames"
import { AlertCircle, ImageIcon, Search, Wrench } from "lucide-react"
import { useState } from "react"
import { MarkdownMessage } from "../../MarkdownMessage"
import type { CommentContext, GroupRenderer, ToolGroup } from "../../events/messageGroups"
import { ImageLightbox } from "../../ui/ImageLightbox"
import { getFileName } from "../../utils/paths"

function getToolLabel(group: ToolGroup): string {
    const input = group.input as Record<string, unknown> | undefined
    if (input) {
        if (group.toolName === "WebSearch" && typeof input.query === "string") {
            const query = input.query.length > 48 ? `${input.query.slice(0, 48)}…` : input.query
            return `${group.toolName}: ${query}`
        }
        if (group.toolName === "Web") {
            const action = webActionLabels(input)[0]
            if (action) return `Web: ${action.length > 48 ? `${action.slice(0, 48)}…` : action}`
            return "Web"
        }
        if (group.toolName === "View image" && typeof input.path === "string") return `View image: ${getFileName(input.path)}`
        // Tools with file_path: Read, Glob, etc.
        if (typeof input.file_path === "string") {
            return `${group.toolName}: ${getFileName(input.file_path)}`
        }
        // Grep: show search pattern
        if (group.toolName === "Grep" && typeof input.pattern === "string") {
            const pattern = input.pattern.length > 30 ? `${input.pattern.slice(0, 30)}…` : input.pattern
            return `${group.toolName}: ${pattern}`
        }
        // Glob: show glob pattern
        if (group.toolName === "Glob" && typeof input.pattern === "string") {
            const pattern = input.pattern.length > 30 ? `${input.pattern.slice(0, 30)}…` : input.pattern
            return `${group.toolName}: ${pattern}`
        }
    }
    return group.toolName
}

function webActionLabels(input: Record<string, unknown>): string[] {
    const labels: string[] = []
    for (const [kind, value] of Object.entries(input)) {
        if (!Array.isArray(value)) continue
        for (const item of value) {
            if (!item || typeof item !== "object" || Array.isArray(item)) continue
            const record = item as Record<string, unknown>
            const label = webActionLabel(kind, record)
            if (label) labels.push(label)
        }
    }
    return labels.slice(0, 12)
}

function webActionLabel(kind: string, input: Record<string, unknown>): string | undefined {
    if (kind === "search_query" && typeof input.q === "string") return `Search: ${input.q}`
    if (kind === "image_query" && typeof input.q === "string") return `Images: ${input.q}`
    if (kind === "open" && typeof input.ref_id === "string") return `Open: ${input.ref_id}`
    if (kind === "find" && typeof input.pattern === "string") return `Find: ${input.pattern}`
    if (kind === "finance" && typeof input.ticker === "string") return `Quote: ${input.ticker}`
    if (kind === "weather" && typeof input.location === "string") return `Weather: ${input.location}`
    if (kind === "time" && typeof input.utc_offset === "string") return `Time: UTC${input.utc_offset}`
    if (kind === "sports" && typeof input.league === "string") {
        return [input.fn, input.league, input.team].filter((value): value is string => typeof value === "string").join(" · ")
    }
    return undefined
}

function ToolMediaContent({ group }: { group: ToolGroup }) {
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
    if (!group.media?.length) return null

    return (
        <>
            <div className="flex gap-2 overflow-x-auto px-3 py-2">
                {group.media.slice(0, 12).map((media, index) => (
                    <button
                        key={`${media.url.slice(0, 48)}-${index}`}
                        type="button"
                        className="h-28 min-w-28 max-w-48 overflow-hidden border border-border bg-base-200 cursor-zoom-in"
                        onClick={() => setLightboxUrl(media.url)}
                    >
                        <img src={media.url} alt={`Tool output ${index + 1}`} loading="lazy" className="h-full w-full object-contain" />
                    </button>
                ))}
            </div>
            {lightboxUrl && <ImageLightbox src={lightboxUrl} alt="Tool output" onClose={() => setLightboxUrl(null)} />}
        </>
    )
}

function WebToolContent({ group, ctx }: { group: ToolGroup; ctx: CommentContext }) {
    const input = group.input && typeof group.input === "object" && !Array.isArray(group.input) ? (group.input as Record<string, unknown>) : {}
    const actions = webActionLabels(input)
    return (
        <div>
            {actions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 px-3 py-2">
                    {actions.map((action, index) => (
                        <span key={`${action}-${index}`} className="border border-border bg-base-200 px-2 py-1 text-xs text-base-content">
                            {action}
                        </span>
                    ))}
                </div>
            )}
            {group.result && group.result.length <= 50_000 ? (
                <div className={cx("px-3 py-2", actions.length > 0 && "border-t border-border")}>
                    <MarkdownMessage text={group.result} taskId={ctx.taskId} commentHandlers={null} variant="plain" density="compact" />
                </div>
            ) : group.result ? (
                <pre className="max-h-64 overflow-auto border-t border-border bg-base-200 px-3 py-2 text-xs whitespace-pre-wrap">{group.result}</pre>
            ) : null}
        </div>
    )
}

function ToolContent({ group, ctx }: { group: ToolGroup; ctx: CommentContext }) {
    if (group.toolName === "Web") return <WebToolContent group={group} ctx={ctx} />
    return (
        <div>
            <div className="px-3 py-2">
                <div className="text-xs text-muted font-medium mb-1">Input</div>
                <pre className="text-xs overflow-x-auto max-h-48 overflow-y-auto p-2 bg-base-200 border border-border whitespace-pre-wrap">
                    {typeof group.input === "string" ? group.input : JSON.stringify(group.input, null, 2)}
                </pre>
            </div>
            <ToolMediaContent group={group} />
            {group.result !== undefined && (
                <div className="px-3 py-2 border-t border-border">
                    <div className={cx("text-xs font-medium mb-1", group.isError ? "text-error" : "text-muted")}>{group.isError ? "Error" : "Result"}</div>
                    <pre
                        className={cx(
                            "text-xs overflow-x-auto max-h-48 overflow-y-auto p-2 border whitespace-pre-wrap",
                            group.isError ? "bg-error/10 border-error/20" : "bg-base-200 border-border"
                        )}
                    >
                        {group.result}
                    </pre>
                </div>
            )}
        </div>
    )
}

export const toolRenderer: GroupRenderer<ToolGroup> = {
    getLabel: getToolLabel,
    getIcon: (group) =>
        group.toolName === "WebSearch" || group.toolName === "Web" ? (
            <Search size="0.85em" className="text-info flex-shrink-0" />
        ) : group.toolName === "View image" ? (
            <ImageIcon size="0.85em" className="text-info flex-shrink-0" />
        ) : (
            <Wrench size="0.85em" className="text-muted flex-shrink-0" />
        ),
    getStatusIcon: (group) => {
        if (group.isError) return <AlertCircle size="1em" className="text-error flex-shrink-0" />
        return null
    },
    getHeaderInfo: () => null,
    renderContent: (group, ctx) => <ToolContent group={group} ctx={ctx} />,
}
